/**
 * Audio-player UI chrome — pure DOM-mutation surface.
 *
 * Single-responsibility module: given a bag of DOM element refs and a
 * time formatter, return a controller that drives every visual aspect
 * of the player (Listen button label/icons, play/pause/loading icons in
 * the player bar, progress fill, thumb position, time readouts, title,
 * thumbnail, .active class on the bar).
 *
 * The controller intentionally holds NO playback state. Callers pass
 * current/duration time when they call `updateSeek`. This keeps the
 * audio-element lifecycle (which will be extracted in a follow-up bite)
 * fully decoupled from the chrome.
 *
 * Hero-image resolution is exposed standalone (`resolveArticleHero`)
 * for callers that want the URL without mutating the DOM (e.g. JSON-LD
 * builders, media-session metadata, social cards).
 *
 * Build path:
 *   src   assets/js/src/single-article/player-ui.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist  assets/dist/js/single-article/player-ui.js  (attaches window.XOT.article.playerUI)
 *
 * @since Phase 2 — JS modernization, bite 11
 */

const HERO_SELECTORS = [
    '.xot-article__hero img',
    '.xot-article__featured img',
    '.post-thumbnail img',
    '.wp-post-image',
] as const;

const TITLE_SELECTORS = '.xot-article__title, h1.entry-title, h1';

export type ButtonState = 'idle' | 'loading' | 'playing';

const BTN_LABELS: Record<ButtonState, string> = {
    idle:    'Listen',
    loading: 'Loading…',
    playing: 'Stop',
};

export interface PlayerUIRefs {
    /** The article-level Listen button. */
    audioBtn?:    HTMLElement | null;
    /** Inner label span inside the Listen button. */
    audioLabel?:  HTMLElement | null;
    /** Three swappable icon nodes — idle / loading / playing. */
    iconPlay?:    HTMLElement | null;
    iconLoading?: HTMLElement | null;
    iconStop?:    HTMLElement | null;
    /** The floating audio player bar. */
    player?:      HTMLElement | null;
    /** Player play/pause toggle button. */
    apPlayPause?: HTMLElement | null;
    /** Three icons inside that button — play / pause / loading. */
    apPlayIcon?:  HTMLElement | null;
    apPauseIcon?: HTMLElement | null;
    apLoadIcon?:  HTMLElement | null;
    /** Seek/scrub track wrapper (carries aria-valuenow). */
    seekWrap?:    HTMLElement | null;
    seekFill?:    HTMLElement | null;
    seekThumb?:   HTMLElement | null;
    /** Mobile-variant progress bar (a second fill the responsive layout uses). */
    mobFill?:     HTMLElement | null;
    timeCur?:     HTMLElement | null;
    timeEl?:      HTMLElement | null;
    /** Player-bar article title + thumbnail elements. */
    titleEl?:     HTMLElement | null;
    apThumb?:     HTMLElement | null;
}

export interface PlayerUIDeps {
    /** Time formatter (typically window.XOT.fmt.time). */
    fmtTime: (seconds: number) => string;
}

export interface PlayerUIController {
    /** Set the Listen button visual + label state. */
    setButtonState(state: ButtonState): void;
    /** Set the player play/pause toggle icon state. */
    setPlayerPlayPause(playing: boolean): void;
    /** Show the spinner state on the player play/pause toggle. */
    setPlayerLoading(): void;
    /** Update the progress fill + thumb + time readouts from a (t, d) pair. */
    updateSeek(currentTime: number, duration: number): void;
    /** Surface the player bar (adds .active, fills title + thumb). */
    showPlayer(): void;
    /** Hide the player bar (removes .active, resets fill + time). */
    hidePlayer(): void;
}

/**
 * Resolve the article hero image URL with priority:
 *   1. `.xot-article__hero img`
 *   2. `.xot-article__featured img`
 *   3. `.post-thumbnail img`
 *   4. `.wp-post-image`
 *   5. `<meta property="og:image">` (always present on article pages)
 *
 * Deliberately does NOT fall back to "first img in article" — that
 * historic selector matched the author avatar.
 */
export function resolveArticleHero(): string {
    if (typeof document === 'undefined') return '';
    for (const sel of HERO_SELECTORS) {
        const el = document.querySelector<HTMLImageElement>(sel);
        if (el?.src) return el.src;
    }
    const og = document.querySelector<HTMLMetaElement>('meta[property="og:image"]');
    return og?.content ?? '';
}

/**
 * Build a controller bound to a set of DOM refs + dependencies.
 * Every method is a safe no-op when its required refs are missing.
 */
export function createPlayerUI(refs: PlayerUIRefs, deps: PlayerUIDeps): PlayerUIController {
    const r = refs;
    const fmt = deps.fmtTime;

    return {
        setButtonState(state) {
            if (r.iconPlay)    r.iconPlay.style.display    = state === 'idle'    ? '' : 'none';
            if (r.iconLoading) r.iconLoading.style.display = state === 'loading' ? '' : 'none';
            if (r.iconStop)    r.iconStop.style.display    = state === 'playing' ? '' : 'none';
            if (r.audioLabel)  r.audioLabel.textContent    = BTN_LABELS[state];
            if (r.audioBtn)    r.audioBtn.classList.toggle('playing', state === 'playing');
        },

        setPlayerPlayPause(playing) {
            if (r.apPlayIcon)  r.apPlayIcon.style.display  = playing ? 'none' : '';
            if (r.apPauseIcon) r.apPauseIcon.style.display = playing ? '' : 'none';
            if (r.apLoadIcon)  r.apLoadIcon.style.display  = 'none';
            r.apPlayPause?.setAttribute('aria-label', playing ? 'Pause' : 'Play');
        },

        setPlayerLoading() {
            if (r.apPlayIcon)  r.apPlayIcon.style.display  = 'none';
            if (r.apPauseIcon) r.apPauseIcon.style.display = 'none';
            if (r.apLoadIcon)  r.apLoadIcon.style.display  = '';
        },

        updateSeek(currentTime, duration) {
            if (!duration || duration <= 0) return;
            const pct = (currentTime / duration) * 100;
            const pctStr = pct + '%';
            if (r.seekFill)  r.seekFill.style.width  = pctStr;
            if (r.seekThumb) r.seekThumb.style.left  = pctStr;
            if (r.mobFill)   r.mobFill.style.width   = pctStr;
            r.seekWrap?.setAttribute('aria-valuenow', String(Math.round(pct)));
            if (r.timeCur)   r.timeCur.textContent   = fmt(currentTime);
            if (r.timeEl)    r.timeEl.textContent    = fmt(duration - currentTime);
        },

        showPlayer() {
            if (!r.player) return;
            r.player.classList.add('active');
            if (r.titleEl) {
                const t = document.querySelector(TITLE_SELECTORS);
                r.titleEl.textContent = t ? (t.textContent ?? '').trim() : document.title;
            }
            if (r.apThumb) {
                const src = resolveArticleHero();
                if (src) r.apThumb.style.backgroundImage = `url(${src})`;
            }
            this.setPlayerPlayPause(true);
        },

        hidePlayer() {
            if (!r.player) return;
            r.player.classList.remove('active');
            if (r.seekFill)  r.seekFill.style.width = '0%';
            if (r.seekThumb) r.seekThumb.style.left = '0%';
            if (r.mobFill)   r.mobFill.style.width  = '0%';
            if (r.timeCur)   r.timeCur.textContent  = '0:00';
            if (r.timeEl)    r.timeEl.textContent   = '0:00';
        },
    };
}

/* ── window.XOT.article.playerUI attachment ────────────────────────── */
interface XotArticlePlayerUI {
    create:             typeof createPlayerUI;
    resolveArticleHero: typeof resolveArticleHero;
}

declare global {
    interface Window {
        XOT?: {
            article?: {
                text?:         unknown;
                highlight?:    unknown;
                progress?:     unknown;
                share?:        unknown;
                audio?:        unknown;
                mediaSession?: unknown;
                dropoff?:      unknown;
                playerUI?:     XotArticlePlayerUI;
            };
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    const xot = (window.XOT ?? {}) as NonNullable<Window['XOT']>;
    const article = (xot.article ?? {}) as NonNullable<NonNullable<Window['XOT']>['article']>;
    article.playerUI = { create: createPlayerUI, resolveArticleHero };
    xot.article = article;
    window.XOT  = xot;
}
